import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getProductoPorCodigo } from "../../services/admin/productos";
import { canonizarCodigoBarra } from "../../utils/codigoBarra";
import ProductViewModal from "../../pages/admin/productos/ProductViewModal";
import "./BuscadorCodigo.css";

/**
 * Buscador por codigo de barras, disponible en todo el panel.
 *
 * Antes vivia dentro de la pantalla de productos: para escanear una pieza
 * habia que estar ahi. Con la pistola en la mano eso es un paso de mas, asi
 * que ahora se abre desde el sidebar o con Ctrl+K desde cualquier pantalla,
 * con el campo ya enfocado: se apunta y se dispara.
 *
 * Al encontrar la pieza abre su ficha en el momento, que es lo que viene a
 * ver quien escanea. Si no la encuentra, el campo queda listo para el
 * siguiente intento sin tener que borrarlo.
 */
function BuscadorCodigo({ abierto, onCerrar }) {
  const [codigo, setCodigo] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [producto, setProducto] = useState(null);

  const campo = useRef(null);

  // Al abrir: campo limpio y enfocado. El foco va en el siguiente cuadro de
  // pintado porque el input todavia no existe cuando cambia el estado.
  useEffect(() => {
    if (!abierto) return;

    setCodigo("");
    setAviso(null);

    const t = setTimeout(() => campo.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;

    const alTeclear = (e) => {
      if (e.key === "Escape") onCerrar?.();
    };

    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [abierto, onCerrar]);

  const buscar = useCallback(
    async (valor) => {
      const crudo = String(valor ?? "").trim();
      if (!crudo) return;

      // La pistola emula un teclado US: con distribucion espanola el guion
      // llega como apostrofo (ADC'000034). Se corrige antes de consultar y
      // se muestra corregido, para no confundir al operario.
      const limpio = canonizarCodigoBarra(crudo) || crudo;
      if (limpio !== crudo) setCodigo(limpio);

      setBuscando(true);
      setAviso(null);

      try {
        const encontrado = await getProductoPorCodigo(limpio);

        if (!encontrado) {
          setAviso({ tipo: "error", texto: `No product matches "${limpio}".` });
          // Se selecciona lo escrito en vez de borrarlo: el siguiente disparo
          // lo reemplaza, y si el codigo estaba mal se puede leer que llego.
          campo.current?.select();
          return;
        }

        setProducto(encontrado);
        setCodigo("");
        onCerrar?.();
      } catch (err) {
        console.error("Error buscando por codigo de barras:", err);
        setAviso({
          tipo: "error",
          texto: err?.message || "Could not look up that barcode.",
        });
        campo.current?.select();
      } finally {
        setBuscando(false);
      }
    },
    [onCerrar]
  );

  return (
    <>
      {abierto
        ? createPortal(
            <div className="bcod-velo" onMouseDown={onCerrar}>
              <div
                className="bcod-caja"
                role="dialog"
                aria-modal="true"
                aria-label="Scan barcode"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="bcod-campo">
                  <span className="bcod-icono" aria-hidden="true">
                    {IconoCodigo}
                  </span>

                  <input
                    ref={campo}
                    type="text"
                    className="bcod-input"
                    placeholder="Scan a barcode or type the code"
                    value={codigo}
                    disabled={buscando}
                    onChange={(e) => {
                      setCodigo(e.target.value);
                      if (aviso) setAviso(null);
                    }}
                    onKeyDown={(e) => {
                      // La pistola manda Enter al terminar de "teclear".
                      if (e.key === "Enter") {
                        e.preventDefault();
                        buscar(codigo);
                      }
                    }}
                  />

                  {buscando ? <span className="bcod-girando" /> : null}
                </div>

                {aviso ? (
                  <p className="bcod-aviso">{aviso.texto}</p>
                ) : (
                  <p className="bcod-pista">
                    Point the scanner and shoot, or type the code and press
                    Enter. <kbd>Esc</kbd> to close.
                  </p>
                )}
              </div>
            </div>,
            document.body
          )
        : null}

      {/* La ficha se abre por fuera del modal: el buscador ya se cerro y lo
          que queda en pantalla es la pieza. */}
      {producto ? (
        <ProductViewModal
          isOpen
          product={producto}
          onClose={() => setProducto(null)}
        />
      ) : null}
    </>
  );
}

/**
 * Gancho para abrir el buscador con Ctrl+K (Cmd+K en Mac).
 *
 * Vive aparte del modal para que el atajo funcione aunque el modal no este
 * montado, y para que el estado lo tenga quien pone el boton en el sidebar.
 */
export function useAtajoBuscador(abrir) {
  useEffect(() => {
    const alTeclear = (e) => {
      const esK = e.key === "k" || e.key === "K";
      if (!esK || !(e.ctrlKey || e.metaKey)) return;

      // Ctrl+K es "insertar enlace" en un editor de texto: si el foco esta
      // escribiendo en algo, no se le roba el atajo.
      const foco = document.activeElement;
      const editando =
        foco &&
        (foco.isContentEditable ||
          /^(INPUT|TEXTAREA|SELECT)$/.test(foco.tagName));

      if (editando) return;

      e.preventDefault();
      abrir();
    };

    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [abrir]);
}

const IconoCodigo = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 6v12M7 6v12M10.5 6v12M14 6v12M17.5 6v12M20 6v12"
      stroke="#475569"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

export default BuscadorCodigo;
